<template>
  <div class="voice-step">
    <el-card shadow="never">
      <template #header>
        <div class="card-header">
          <span>语音合成</span>
          <div class="header-actions">
            <el-select v-model="defaultSpeaker" placeholder="选择默认音色" style="width: 150px; margin-right: 10px;">
              <el-option
                v-for="(speaker, key) in availableSpeakers"
                :key="key"
                :label="speaker.name"
                :value="key"
              />
            </el-select>
            <el-button type="primary" @click="generateAllVoices" :loading="generatingAll">
              <el-icon><Microphone /></el-icon>
              批量生成配音
            </el-button>
          </div>
        </div>
      </template>

      <el-empty v-if="!safeScenes.length" description="暂无剧本数据">
        <p>请先在剧本拆解步骤生成剧本</p>
      </el-empty>

      <div v-else class="voice-content">
        <el-table :data="safeScenes" style="width: 100%" v-loading="loading">
          <el-table-column type="index" label="场景" width="60" />
          <el-table-column label="标题" width="120">
            <template #default="{ row }">
              {{ row.title || `场景${row.scene_number}` }}
            </template>
          </el-table-column>
          <el-table-column label="台词/旁白" min-width="200">
            <template #default="{ row }">
              <div v-if="row.dialogue && row.dialogue.length > 0">
                <div v-for="(line, idx) in row.dialogue.slice(0, 3)" :key="idx" class="dialogue-preview">
                  <span class="speaker">{{ line.speaker }}:</span>
                  <span class="content">{{ line.content }}</span>
                </div>
                <el-tag v-if="row.dialogue.length > 3" size="small" type="info">
                  +{{ row.dialogue.length - 3 }} 句
                </el-tag>
              </div>
              <div v-else-if="row.narration" class="narration-preview">
                {{ row.narration }}
              </div>
              <span v-else class="no-content">无台词</span>
            </template>
          </el-table-column>
          <el-table-column label="角色配音" min-width="380">
            <template #default="{ row }">
              <div v-if="hasVoiceContent(row)" class="character-voice-list">
                <!-- 有台词的角色 -->
                <div
                  v-for="charVoice in getCharacterVoices(row)"
                  :key="charVoice.character_name"
                  class="character-voice-row"
                >
                  <span class="char-name">{{ charVoice.character_name }}</span>
                  <el-select
                    v-model="charVoice.speaker"
                    size="small"
                    style="width: 110px;"
                    placeholder="选择音色"
                  >
                    <el-option
                      v-for="(sp, key) in filteredSpeakers(charVoice.genderHint)"
                      :key="key"
                      :label="sp.name"
                      :value="key"
                    />
                  </el-select>
                  <el-tag v-if="charVoice.voice_url" type="success" size="small" class="voice-status-tag">
                    {{ charVoice.duration ? `${Number(charVoice.duration).toFixed(1)}秒` : '已生成' }}
                  </el-tag>
                  <el-tag v-else type="info" size="small" class="voice-status-tag">待生成</el-tag>
                  <el-button
                    v-if="charVoice.voice_url"
                    type="primary"
                    size="small"
                    link
                    @click="playAudio(charVoice.voice_url)"
                  >
                    试听
                  </el-button>
                  <el-button
                    type="success"
                    size="small"
                    link
                    :loading="charVoice.generating"
                    @click="generateCharacterVoice(row, charVoice)"
                  >
                    {{ charVoice.voice_url ? '重新生成' : '生成' }}
                  </el-button>
                </div>
                <!-- 旁白（如果没有台词） -->
                <div v-if="!row.dialogue || row.dialogue.length === 0" class="character-voice-row">
                  <span class="char-name">旁白</span>
                  <el-select
                    v-model="row.narrationSpeaker"
                    size="small"
                    style="width: 110px;"
                    placeholder="选择音色"
                  >
                    <el-option
                      v-for="(sp, key) in availableSpeakers"
                      :key="key"
                      :label="sp.name"
                      :value="key"
                    />
                  </el-select>
                  <el-tag v-if="row.voice_url" type="success" size="small" class="voice-status-tag">
                    {{ row.voice_duration ? `${Number(row.voice_duration).toFixed(1)}秒` : '已生成' }}
                  </el-tag>
                  <el-tag v-else type="info" size="small" class="voice-status-tag">待生成</el-tag>
                  <el-button
                    v-if="row.voice_url"
                    type="primary"
                    size="small"
                    link
                    @click="playAudio(row.voice_url)"
                  >
                    试听
                  </el-button>
                  <el-button
                    type="success"
                    size="small"
                    link
                    :loading="row.generating"
                    @click="generateNarrationVoice(row)"
                  >
                    {{ row.voice_url ? '重新生成' : '生成' }}
                  </el-button>
                </div>
              </div>
              <el-tag v-else type="info" size="small">无内容</el-tag>
            </template>
          </el-table-column>
        </el-table>

        <div class="voice-stats" v-if="voiceStats.total > 0">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-statistic title="总场景数" :value="voiceStats.total" />
            </el-col>
            <el-col :span="8">
              <el-statistic title="已生成角色" :value="voiceStats.generated" />
            </el-col>
            <el-col :span="8">
              <el-statistic title="总时长" :value="Number(voiceStats.totalDuration.toFixed(1))" suffix="秒" />
            </el-col>
          </el-row>
        </div>

        <div class="actions">
          <el-button type="success" @click="confirmVoice" size="large" :disabled="voiceStats.generated === 0">
            <el-icon><Check /></el-icon>
            确认配音并继续
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 音频播放器 -->
    <audio ref="audioPlayer" style="display: none;"></audio>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import { useProjectStore } from '../../stores/project'
import { getScripts, generateVoice, getVoices } from '../../api/project'
import api from '../../api/index.js'

const props = defineProps({
  projectId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['step-complete', 'step-save'])

const projectStore = useProjectStore()
const scenes = ref([])
const loading = ref(false)
const generatingAll = ref(false)
const defaultSpeaker = ref('Cherry')
const audioPlayer = ref(null)

// 确保 scenes 始终是数组
const safeScenes = computed(() => scenes.value || [])

// 可用音色列表
const availableSpeakers = {
  'Cherry': { name: '芊悦', gender: '女', description: '阳光积极、亲切自然小姐姐' },
  'Serena': { name: '苏瑶', gender: '女', description: '温柔小姐姐' },
  'Ethan': { name: '晨煦', gender: '男', description: '标准普通话，阳光、温暖、活力、朝气' },
  'Chelsie': { name: '千雪', gender: '女', description: '二次元虚拟女友' },
  'Momo': { name: '茉兔', gender: '女', description: '撒娇搞怪，逗你开心' },
  'Vivian': { name: '十三', gender: '女', description: '拽拽的、可爱的小暴躁' },
  'Moon': { name: '月白', gender: '男', description: '率性帅气的月白' },
  'Maia': { name: '四月', gender: '女', description: '知性与温柔的碰撞' },
  'Kai': { name: '凯', gender: '男', description: '耳朵的一场SPA' },
  'Eldric Sage': { name: '沧明子', gender: '男', description: '沉稳睿智的老者' },
  'Mochi': { name: '沙小弥', gender: '男', description: '聪明伶俐的小大人' },
  'Mia': { name: '乖小妹', gender: '女', description: '温顺如春水，乖巧如初雪' }
}

// 根据性别提示过滤音色
const filteredSpeakers = (genderHint) => {
  if (!genderHint) return availableSpeakers
  const result = {}
  for (const [key, sp] of Object.entries(availableSpeakers)) {
    if (sp.gender === genderHint) {
      result[key] = sp
    }
  }
  // 如果过滤后为空，返回全部
  return Object.keys(result).length > 0 ? result : availableSpeakers
}

// 获取场景中每个角色的语音状态
// 结果缓存在 row._charVoiceMap 中，避免每次渲染重建对象
const getCharacterVoices = (row) => {
  if (!row.dialogue || row.dialogue.length === 0) return []

  // 按角色分组台词
  const charMap = {}
  for (const line of row.dialogue) {
    const name = line.speaker || '未知角色'
    if (!charMap[name]) {
      charMap[name] = {
        character_name: name,
        lines: [],
        speaker: defaultSpeaker.value,
        voice_url: '',
        duration: 0,
        generating: false,
        genderHint: null
      }
    }
    charMap[name].lines.push(line)
  }

  // 从角色设定中推断性别
  if (row.characters && Array.isArray(row.characters)) {
    for (const char of row.characters) {
      const name = typeof char === 'string' ? char : (char.name || char.character_name || '')
      if (charMap[name]) {
        const gender = typeof char === 'object' ? (char.gender || char.sex || null) : null
        if (gender) {
          charMap[name].genderHint = gender.includes('女') ? '女' : '男'
        }
      }
    }
  }

  // 从已有的语音记录中恢复状态（仅首次或未缓存时）
  if (!row._charVoiceMap) {
    row._charVoiceMap = {}
    if (row.characterVoices && Array.isArray(row.characterVoices)) {
      for (const cv of row.characterVoices) {
        const name = cv.character_name || ''
        if (charMap[name]) {
          charMap[name].voice_url = cv.mp3_url || cv.url || ''
          charMap[name].duration = cv.duration || 0
          charMap[name].speaker = cv.speaker || defaultSpeaker.value
          charMap[name].voice_id = cv.id || cv.voice_id || null
          charMap[name].is_selected = cv.is_selected || 0
        }
      }
    }
    // 缓存初始状态
    for (const [name, data] of Object.entries(charMap)) {
      row._charVoiceMap[name] = data
    }
  } else {
    // 已有缓存：保留用户修改的 speaker 和生成结果，但更新 lines
    for (const [name, data] of Object.entries(charMap)) {
      if (row._charVoiceMap[name]) {
        // 保留已有的状态（speaker选择、voice_url等），只更新lines
        row._charVoiceMap[name].lines = data.lines
      } else {
        // 新角色
        row._charVoiceMap[name] = data
      }
    }
  }

  return Object.values(row._charVoiceMap)
}

// 统计信息
const voiceStats = computed(() => {
  const scenesList = safeScenes.value
  if (!scenesList || !Array.isArray(scenesList)) {
    return { total: 0, generated: 0, totalDuration: 0 }
  }
  const total = scenesList.length
  let generated = 0
  let totalDuration = 0

  for (const s of scenesList) {
    const charVoices = getCharacterVoices(s)
    if (charVoices.length > 0) {
      // 有台词的场景：检查每个角色是否都有语音
      const allGenerated = charVoices.every(cv => cv.voice_url)
      if (allGenerated) generated++
      totalDuration += charVoices.reduce((sum, cv) => sum + (cv.duration || 0), 0)
    } else if (s.voice_url) {
      // 旁白场景
      generated++
      totalDuration += s.voice_duration || 0
    }
  }

  return { total, generated, totalDuration }
})

// 加载剧本和语音数据
onMounted(async () => {
  await loadScenesAndVoices()
})

const loadScenesAndVoices = async () => {
  loading.value = true
  try {
    // 加载剧本场景
    const scriptsResponse = await getScripts(props.projectId)

    // 加载已生成的语音
    const voicesResponse = await getVoices(props.projectId)

    // 创建语音查找表（按 script_id + character_name 分组）
    const voiceMap = {}
    if (voicesResponse && Array.isArray(voicesResponse)) {
      voicesResponse.forEach(voice => {
        if (voice.script_id) {
          if (!voiceMap[voice.script_id]) {
            voiceMap[voice.script_id] = []
          }
          voiceMap[voice.script_id].push(voice)
        }
      })
    }

    if (scriptsResponse && scriptsResponse.length > 0) {
      scenes.value = scriptsResponse.map((script, index) => {
        const characterVoices = voiceMap[script.id] || []

        return {
          id: script.id,
          scene_number: index + 1,
          title: script.scene_title || '',
          description: script.scene_description || '',
          narration: script.narration || '',
          dialogue: script.dialogue || [],
          characters: script.characters_present || [],
          location: script.location || '',
          // 语音相关字段
          characterVoices: characterVoices,
          // 旁白场景的语音（兼容旧逻辑）
          voice_id: characterVoices.length > 0 ? characterVoices[0].id : null,
          voice_url: characterVoices.length === 1 && !characterVoices[0].character_name ? (characterVoices[0].mp3_url || '') : '',
          voice_duration: characterVoices.length === 1 && !characterVoices[0].character_name ? (characterVoices[0].duration || 0) : 0,
          narrationSpeaker: defaultSpeaker.value,
          generating: false
        }
      })
    }
  } catch (error) {
    console.error('Load scenes and voices error:', error)
    ElMessage.error('加载数据失败')
  } finally {
    loading.value = false
  }
}

// 为单个角色生成语音
const generateCharacterVoice = async (row, charVoice) => {
  if (!row.id) {
    ElMessage.warning('场景ID不存在')
    return
  }

  // 拼接该角色的所有台词
  const text = charVoice.lines.map(d => d.content).join('。')
  if (!text) {
    ElMessage.warning('该角色没有可合成的台词')
    return
  }

  charVoice.generating = true
  try {
    const response = await generateVoice(row.id, charVoice.speaker, text, charVoice.character_name)

    if (response && response.voice_url) {
      charVoice.voice_url = response.voice_url
      charVoice.duration = response.duration || 0
      charVoice.voice_id = response.voice_id
      charVoice.is_selected = 1

      // 同步更新 row.characterVoices 数组，确保 Vue 响应式更新
      if (!row.characterVoices) row.characterVoices = []
      const existingIdx = row.characterVoices.findIndex(cv => cv.character_name === charVoice.character_name)
      const newRecord = {
        id: response.voice_id,
        character_name: charVoice.character_name,
        mp3_url: response.voice_url,
        duration: response.duration || 0,
        speaker: charVoice.speaker,
        is_selected: 1
      }
      if (existingIdx >= 0) {
        row.characterVoices[existingIdx] = newRecord
      } else {
        row.characterVoices.push(newRecord)
      }

      ElMessage.success(`${charVoice.character_name} 语音生成成功`)
    } else {
      ElMessage.warning('语音生成失败，请重试')
    }
  } catch (error) {
    console.error('Generate voice error:', error)
    ElMessage.error('语音生成失败：' + (error.message || '未知错误'))
  } finally {
    charVoice.generating = false
  }
}

// 为旁白生成语音
const generateNarrationVoice = async (row) => {
  if (!row.id) {
    ElMessage.warning('场景ID不存在')
    return
  }

  const text = row.narration || ''
  if (!text.trim()) {
    ElMessage.warning('没有旁白内容')
    return
  }

  row.generating = true
  try {
    const response = await generateVoice(row.id, row.narrationSpeaker || defaultSpeaker.value, text, '旁白')

    if (response && response.voice_url) {
      row.voice_url = response.voice_url
      row.voice_duration = response.duration || 0
      row.voice_id = response.voice_id
      ElMessage.success('旁白语音生成成功')
    } else {
      ElMessage.warning('语音生成失败，请重试')
    }
  } catch (error) {
    console.error('Generate voice error:', error)
    ElMessage.error('语音生成失败：' + (error.message || '未知错误'))
  } finally {
    row.generating = false
  }
}

// 批量生成所有语音
const generateAllVoices = async () => {
  generatingAll.value = true
  let successCount = 0
  let failCount = 0

  for (const row of scenes.value) {
    // 有台词的场景：为每个角色生成
    if (row.dialogue && row.dialogue.length > 0) {
      const charVoices = getCharacterVoices(row)
      for (const cv of charVoices) {
        if (!cv.voice_url && !cv.generating) {
          try {
            await generateCharacterVoice(row, cv)
            if (cv.voice_url) {
              successCount++
            } else {
              failCount++
            }
          } catch (error) {
            failCount++
          }
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }
    } else if (row.narration && !row.voice_url && !row.generating) {
      // 旁白场景
      try {
        await generateNarrationVoice(row)
        if (row.voice_url) {
          successCount++
        } else {
          failCount++
        }
      } catch (error) {
        failCount++
      }
      await new Promise(resolve => setTimeout(resolve, 500))
    }
  }

  generatingAll.value = false

  if (successCount > 0) {
    ElMessage.success(`成功生成 ${successCount} 个角色的语音`)
  }
  if (failCount > 0) {
    ElMessage.warning(`${failCount} 个角色生成失败`)
  }
}

// 播放音频
const playAudio = (url) => {
  if (audioPlayer.value) {
    audioPlayer.value.src = url
    audioPlayer.value.play()
  }
}

// 检查场景是否有可合成的内容
const hasVoiceContent = (row) => {
  return (row.dialogue && row.dialogue.length > 0) || (row.narration && row.narration.trim())
}

// 确认配音
const confirmVoice = async () => {
  if (voiceStats.value.generated === 0) {
    ElMessage.warning('请至少生成一个场景的语音')
    return
  }

  try {
    // 更新项目状态为语音合成完成
    await projectStore.updateProjectStatus('voice_generated')
    ElMessage.success('配音确认成功')
    emit('step-complete')
  } catch (error) {
    console.error('Confirm voice error:', error)
    ElMessage.error('状态更新失败')
  }
}
</script>

<style scoped lang="scss">
.voice-step {
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .header-actions {
    display: flex;
    align-items: center;
  }
}

.character-voice-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.character-voice-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;

  .char-name {
    font-weight: 600;
    color: #409eff;
    min-width: 60px;
    font-size: 13px;
  }

  .voice-status-tag {
    min-width: 50px;
    text-align: center;
  }
}

.dialogue-preview {
  margin: 4px 0;
  font-size: 13px;

  .speaker {
    font-weight: 600;
    color: #409eff;
    margin-right: 4px;
  }

  .content {
    color: #606266;
  }
}

.narration-preview {
  color: #606266;
  font-size: 13px;
  line-height: 1.5;
}

.no-content {
  color: #909399;
  font-style: italic;
}

.voice-stats {
  margin-top: 24px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 4px;
}

.actions {
  margin-top: 24px;
  text-align: center;
}
</style>
